import { NormalizedFrame, DroneFeedAdapter } from './drone_integration/DroneFeedAdapter';
import { supabase } from '@/integrations/supabase/client';

export interface DamageDetectionResult {
  annotations: Array<{
    id: string;
    type: string;          // e.g. 'hail', 'missing_shingle', 'soft_metal_damage'
    confidence: number;    // 0–1
    bbox: [number, number, number, number]; // x, y, width, height
  }>;
  summary: {
    totalFindings: number;
    hailCount: number;
    otherDamageCount: number;
  };
  rawAnalysis: {
    damage_type: string;
    linear_measurement_ft: number;
    confidence_score: number;
    compliance_alert: string;
  };
}

/**
 * ARAnalyzer - Unified damage detection for phone camera AND drone feeds
 * Reuses existing analyze-live-camera-feed backend logic
 */
export class ARAnalyzer {
  /**
   * Process a normalized frame (from phone OR drone) through the existing AR damage detection engine
   */
  static async processFrame(frame: NormalizedFrame): Promise<DamageDetectionResult> {
    console.log('🔍 ARAnalyzer.processFrame - source:', frame.metadata.source);

    try {
      // Convert frame buffer to base64 for API transmission
      const base64Image = frame.frameBuffer.toString('base64');
      const imageDataUrl = `data:image/jpeg;base64,${base64Image}`;

      // Build context string with metadata
      const contextParts = [];
      if (frame.metadata.source) contextParts.push(`Source: ${frame.metadata.source}`);
      if (frame.metadata.altitudeMeters) contextParts.push(`Altitude: ${frame.metadata.altitudeMeters.toFixed(1)}m`);
      if (frame.metadata.gps) contextParts.push(`GPS: ${frame.metadata.gps.lat.toFixed(6)}, ${frame.metadata.gps.lng.toFixed(6)}`);
      const context = contextParts.join(' | ');

      // Call existing AR damage detection backend
      const { data, error } = await supabase.functions.invoke('analyze-live-camera-feed', {
        body: { 
          image: imageDataUrl, 
          context 
        }
      });

      if (error) throw error;

      // Transform raw analysis into structured detection result
      const result = this.transformAnalysis(data, frame);
      
      console.log('✅ ARAnalyzer result:', result.summary);
      return result;

    } catch (error) {
      console.error('❌ ARAnalyzer.processFrame error:', error);
      
      // Return error state
      return {
        annotations: [],
        summary: {
          totalFindings: 0,
          hailCount: 0,
          otherDamageCount: 0
        },
        rawAnalysis: {
          damage_type: 'Error',
          linear_measurement_ft: 0,
          confidence_score: 0,
          compliance_alert: error instanceof Error ? error.message : 'Analysis failed'
        }
      };
    }
  }

  /**
   * Transform raw camera analysis into structured damage detection result
   */
  private static transformAnalysis(rawAnalysis: any, frame: NormalizedFrame): DamageDetectionResult {
    const annotations = [];
    const summary = {
      totalFindings: 0,
      hailCount: 0,
      otherDamageCount: 0
    };

    // If damage detected with sufficient confidence, create annotation (LOWERED THRESHOLD)
    if (rawAnalysis.damage_type && rawAnalysis.damage_type !== 'No Damage' && rawAnalysis.damage_type !== 'Error' && rawAnalysis.confidence_score >= 0.30) {
      const annotation = {
        id: `${Date.now()}-${rawAnalysis.damage_type.replace(/\s+/g, '-')}`,
        type: rawAnalysis.damage_type,
        confidence: rawAnalysis.confidence_score,
        // Simulate bbox (in real implementation, vision model would provide this)
        bbox: [0.2, 0.2, 0.6, 0.6] as [number, number, number, number]
      };

      annotations.push(annotation);
      summary.totalFindings = 1;

      if (rawAnalysis.damage_type.toLowerCase().includes('hail')) {
        summary.hailCount = 1;
      } else {
        summary.otherDamageCount = 1;
      }
    }

    return {
      annotations,
      summary,
      rawAnalysis: {
        damage_type: rawAnalysis.damage_type || 'Unknown',
        linear_measurement_ft: rawAnalysis.linear_measurement_ft || 0,
        confidence_score: rawAnalysis.confidence_score || 0,
        compliance_alert: rawAnalysis.compliance_alert || ''
      }
    };
  }

  /**
   * Analyze a base64 image directly (convenience method)
   */
  static async processBase64Image(base64Image: string, source: 'DRONE' | 'TEST' | 'PHONE' = 'PHONE'): Promise<DamageDetectionResult> {
    console.log('🔍 ARAnalyzer.processBase64Image - source:', source);

    try {
      // Validate image is not empty or suspiciously small
      if (!base64Image || base64Image.length < 1000) {
        console.warn('⚠️ Image too small or empty, skipping analysis');
        return {
          annotations: [],
          summary: { totalFindings: 0, hailCount: 0, otherDamageCount: 0 },
          rawAnalysis: { 
            damage_type: 'No Damage', 
            linear_measurement_ft: 0,
            confidence_score: 0,
            compliance_alert: 'Image too small to analyze'
          }
        };
      }

      const imageDataUrl = base64Image.startsWith('data:image')
        ? base64Image
        : `data:image/jpeg;base64,${base64Image}`;

      const context = `Source: ${source}`;

      const { data, error } = await supabase.functions.invoke('analyze-live-camera-feed', {
        body: {
          image: imageDataUrl,
          context,
        },
      });

      // If there's an error or API rejection, return "No Damage" to keep stream flowing
      if (error) {
        console.warn('⚠️ Analysis API error (skipping frame):', error.message);
        return {
          annotations: [],
          summary: { totalFindings: 0, hailCount: 0, otherDamageCount: 0 },
          rawAnalysis: { 
            damage_type: 'No Damage',
            linear_measurement_ft: 0, 
            confidence_score: 0,
            compliance_alert: 'Frame skipped due to analysis error'
          }
        };
      }

      // If API returned error state, also return "No Damage" instead of breaking
      if (data?.damage_type === 'Error') {
        console.warn('⚠️ Analysis returned error state (skipping frame):', data.compliance_alert);
        return {
          annotations: [],
          summary: { totalFindings: 0, hailCount: 0, otherDamageCount: 0 },
          rawAnalysis: { 
            damage_type: 'No Damage',
            linear_measurement_ft: 0,
            confidence_score: 0,
            compliance_alert: 'Frame skipped - image format issue'
          }
        };
      }

      const result = this.transformAnalysis(data, {
        frameBuffer: undefined as any,
        metadata: {
          gps: null,
          altitudeMeters: null,
          timestamp: new Date(),
          source,
        },
      });

      console.log('✅ ARAnalyzer.processBase64Image result:', result.summary);
      return result;
    } catch (error) {
      console.error('❌ ARAnalyzer.processBase64Image error:', error);
      // Return "No Damage" instead of error to keep stream flowing
      return {
        annotations: [],
        summary: {
          totalFindings: 0,
          hailCount: 0,
          otherDamageCount: 0,
        },
        rawAnalysis: {
          damage_type: 'No Damage',
          linear_measurement_ft: 0,
          confidence_score: 0,
          compliance_alert: 'Frame analysis failed',
        },
      };
    }
   }
}
